import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { fetchChecklists, type Checklist } from "./checklistsClient";
import { type Job, type JobStatus, type SubmitJobRequest, type SubmitJobResponse, jobsApi } from "./jobsClient";
import { jobsQueryKey } from "./queryKeys";
import { useAuth } from "../auth/AuthContext";
import { useProject } from "../project/ProjectContext";

/**
 * Reads the user's job list. Polls every 5s while at least one job is
 * non-terminal so the history page reflects state changes without
 * forcing a refresh.
 *
 * "Non-terminal" includes one extra case beyond the obvious queued /
 * running / cancelling: a row with terminal `status` but no
 * `succeededCount` yet. That happens during the window between the
 * worker flipping `status` to `completed`/`failed`/`cancelled` and the
 * downstream finalize Lambda writing `resultS3Key`, `failuresS3Key`,
 * `succeededCount`, and `failedCount` from S3. Without this guard the
 * SPA can catch a row mid-finalize, cache the incomplete snapshot, and
 * stop polling — which is how the user ends up seeing a `completed`
 * job with blank counts and no download buttons.
 */
export function useJobsList(): {
    jobs: Job[];
    isLoading: boolean;
    error: Error | null;
} {
    const { jwt } = useAuth();
    const queryOptions: UseQueryOptions<{ jobs: Job[] }, Error> = {
        queryKey: jobsQueryKey.list(),
        queryFn: () => jobsApi.list(),
        enabled: jwt !== null,
        refetchInterval: (q) => {
            const data = q.state.data;
            if (!data) return false;
            const hasActive = data.jobs.some(isStillResolving);
            return hasActive ? 5_000 : false;
        },
    };
    const query = useQuery(queryOptions);
    return {
        jobs: query.data?.jobs ?? [],
        isLoading: query.isLoading,
        error: query.error,
    };
}

/**
 * Exhaustive status→poll decision (PRD-001 rev 13, panel: neo). The `never`
 * check makes the compiler flag every new JobStatus member until this
 * function handles it — `.includes` lists silently treat new states as
 * "not resolving", which is exactly how a `gathering` job would freeze.
 */
export function statusPollBehavior(status: JobStatus): "poll" | "static" {
    switch (status) {
        case "queued":
        case "gathering": // matched-so-far count advances (PRD §Job history)
        case "running":
        case "cancelling":
            return "poll";
        case "awaiting_confirmation": // waits on a human; the confirm mutation invalidates
        case "completed":
        case "failed":
        case "cancelled":
        case "expired":
            return "static";
        default: {
            const exhaustive: never = status;
            return exhaustive;
        }
    }
}

export function isStillResolving(job: Job): boolean {
    if (statusPollBehavior(job.status) === "poll") return true;
    // Terminal status but finalize hasn't written the counts yet —
    // succeededCount is the authoritative "finalize ran" marker.
    // `expired` is excluded: by the time a job has been around long
    // enough to expire, finalize has long since run; if succeededCount
    // is still missing on an expired row, no future poll will fix it.
    // Rev 13 (panel: trinity B1): jobs that terminated during gathering /
    // awaiting-confirmation carry `terminalPhase` and NEVER get finalize
    // counts — they are settled, or the page would poll them forever.
    if (["completed", "failed", "cancelled"].includes(job.status)) {
        if (job.terminalPhase) return false;
        return typeof job.succeededCount !== "number";
    }
    return false;
}

export function useChecklists(): {
    checklists: Checklist[];
    isLoading: boolean;
    error: Error | null;
} {
    const { selectedProjectId } = useProject();
    const query = useQuery({
        queryKey: selectedProjectId ? jobsQueryKey.checklists(selectedProjectId) : ["jobs", "checklists", "none"],
        queryFn: fetchChecklists,
        enabled: selectedProjectId !== null,
    });
    return {
        checklists: query.data ?? [],
        isLoading: query.isLoading,
        error: query.error,
    };
}

export function useSubmitJob() {
    const queryClient = useQueryClient();
    return useMutation<SubmitJobResponse, Error, SubmitJobRequest>({
        mutationFn: (req) => jobsApi.submit(req),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: jobsQueryKey.list() });
        },
    });
}

export function useCancelJob() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (jobId: string) => jobsApi.cancel(jobId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: jobsQueryKey.list() });
        },
    });
}

export function useRerunJob() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (jobId: string) => jobsApi.rerun(jobId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: jobsQueryKey.list() });
        },
    });
}

/** PRD-001 rev 13 §Large-cohort gate: Continue on an awaiting-confirmation job. */
export function useConfirmJob() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (jobId: string) => jobsApi.confirm(jobId),
        onSettled: () => {
            // Invalidate on failure too — a 409 means the row moved on
            // (cancel/auto-cancel won the race) and the list is stale.
            void queryClient.invalidateQueries({ queryKey: jobsQueryKey.list() });
        },
    });
}
